import { useCallback, useEffect, useMemo } from 'react';

import {
  CacheGroupedItemsCurrencies,
  CacheGroupedItemsGroupByEnum,
  FetchGroupedItemsCacheKey,
  GroupedItemsCurrencies,
  GroupedItemsGroupByEnum
} from '../../../../../groupedItems/groupedItemsTypes';
import {
  InvoiceCurrencyRateID,
  InvoiceNanoID
} from '../../../../invoicesTypes';
import { Currencies, UUID } from '../../../../../../types';
import {
  ProformaInvoiceFormGroupByMode,
  ProformaInvoiceFormViewMode
} from '../../../../../teams/components/forms/ProformaInvoiceForm/ProformaInvoiceForm.types';
import {
  FETCH_GROUPED_INVOICE_ITEMS_QUERY,
  FetchedGroupedInvoiceItemsQueryResponse
} from '../../../../../groupedItems/queries/fetchGroupedInvoiceItems.query';
import {
  CACHE_FETCH_GROUPED_INVOICE_ITEMS_QUERY,
  FetchedCacheGroupedInvoiceItemsQueryResponse,
  FetchedCacheGroupedInvoiceItemsQueryResponseMeta
} from '../../../../../groupedItems/queries/fetchCacheGroupedInvoiceItems.query';

import { usePreviousValue } from '../../../../../../common/hooks/usePreviousValue';
import { useCacheProformaGroupedItems } from '../../../../../groupedItems/hooks/useCacheProformaGroupedItems';

import { GroupedItemCache } from '../../../../../groupedItems/GroupedItemCache';
import { getGroupByFromViewMode } from '../../../../../teams/components/forms/ProformaInvoiceForm/utils/getGroupByFromViewMode';
import { getSortFromViewMode } from '../../../../../teams/components/forms/ProformaInvoiceForm/utils/getSortFromViewMode';
import { GroupedItemsPermissions } from '../../../../../groupedItems/groupedItemsConstants';
import { CacheInfiniteIndexQueryOnSuccess } from '../../../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

const groupedItemsInitialLimit = 1000;

interface InvoiceItemsViewItemsOptions {
  invoiceNanoId: InvoiceNanoID;
  currencyRateId: InvoiceCurrencyRateID;
  currency: Currencies;
  viewMode: ProformaInvoiceFormViewMode;
  groupBy: ProformaInvoiceFormGroupByMode;
  enableGrouping: boolean;
  cacheApiEnabled: boolean;
  onSuccess?: CacheInfiniteIndexQueryOnSuccess<
    (
      | FetchedGroupedInvoiceItemsQueryResponse
      | FetchedCacheGroupedInvoiceItemsQueryResponse
    ) & {
      uuid: UUID;
    },
    FetchedCacheGroupedInvoiceItemsQueryResponseMeta
  >;
}

function useInvoiceItemsViewItems({
  invoiceNanoId,
  currencyRateId,
  currency,
  viewMode,
  groupBy,
  enableGrouping,
  cacheApiEnabled,
  onSuccess
}: InvoiceItemsViewItemsOptions) {
  const cacheKey = GroupedItemCache.invoiceGroupedItemsCacheKey(invoiceNanoId);

  const cacheKeys = useMemo<FetchGroupedItemsCacheKey[]>(
    () => [cacheKey],
    [cacheKey]
  );

  // this params needs update in download_pdf also
  const additionalParams = useMemo(() => {
    const groupByParam = getGroupByFromViewMode({
      viewMode,
      groupByMode: groupBy,
      enableGrouping,
      mergeSimilar: true,
      withItemSplit: true,
      cacheApiEnabled
    });

    return {
      convertTo: cacheApiEnabled
        ? (currency as CacheGroupedItemsCurrencies)
        : (currency.toLocaleLowerCase() as GroupedItemsCurrencies),
      currencyRateId,
      groupBy: cacheApiEnabled
        ? (groupByParam?.map((field) =>
            field.toUpperCase()
          ) as CacheGroupedItemsGroupByEnum[])
        : (groupByParam as GroupedItemsGroupByEnum[]),
      groupItemsByType: viewMode === ProformaInvoiceFormViewMode.INVOICES,
      showTotal: true,
      sort: getSortFromViewMode(
        viewMode,
        groupBy,
        enableGrouping,
        cacheApiEnabled
      )
    };
  }, [
    cacheApiEnabled,
    currency,
    currencyRateId,
    enableGrouping,
    groupBy,
    viewMode
  ]);

  // this params needs update in download_pdf also
  const initialFilters = useMemo(
    () => ({
      parentOrTempInvoiceNanoId: {
        eq: invoiceNanoId
      }
    }),
    [invoiceNanoId]
  );

  const prevFilters = usePreviousValue(initialFilters);

  const {
    groupedItems,
    groupedItemsFetched,
    groupedItemsFilters,
    groupedItemsSort,
    groupedItemsLoading,
    groupedItemsError,
    filterGroupedItems,
    sortGroupedItems,
    groupedItemsHasNextPage,
    loadMoreGroupedItems,
    groupedItemsFetchingNextPage,
    groupedItemsMeta
  } = useCacheProformaGroupedItems<
    (
      | FetchedGroupedInvoiceItemsQueryResponse
      | FetchedCacheGroupedInvoiceItemsQueryResponse
    ) & {
      uuid: UUID;
    },
    FetchedCacheGroupedInvoiceItemsQueryResponseMeta
  >({
    action: GroupedItemsPermissions.READ_CACHE_INVOICE_GROUPED_ITEMS_QUERY,
    query: FETCH_GROUPED_INVOICE_ITEMS_QUERY,
    cacheQuery: cacheApiEnabled
      ? CACHE_FETCH_GROUPED_INVOICE_ITEMS_QUERY
      : undefined,
    cacheKey,
    initialFilters,
    initialLimit: groupedItemsInitialLimit,
    initialSort: getSortFromViewMode(
      viewMode,
      groupBy,
      enableGrouping,
      cacheApiEnabled
    ),
    additionalParams,
    options: {
      enabled: !!invoiceNanoId,
      onSuccess
    },
    trackTotalHits: true,
    withMetadata: true
  });

  useEffect(() => {
    if (prevFilters !== initialFilters) {
      filterGroupedItems(initialFilters);
    }
  }, [prevFilters, filterGroupedItems, initialFilters]);

  useEffect(() => {
    if (groupedItemsHasNextPage && !groupedItemsFetchingNextPage) {
      void loadMoreGroupedItems();
    }
  }, [
    groupedItemsHasNextPage,
    groupedItemsFetchingNextPage,
    loadMoreGroupedItems
  ]);

  const handleClearGroupedItemsFilters = useCallback(() => {
    filterGroupedItems(initialFilters);
  }, [filterGroupedItems, initialFilters]);

  return {
    initialFilters,
    cacheKeys,
    groupedItemsLoading,
    groupedItemsFetched,
    groupedItemsError,
    groupedItems,
    groupedItemsSort,
    sortGroupedItems,
    groupedItemsFilters,
    filterGroupedItems,
    handleClearGroupedItemsFilters,
    groupedItemsMeta
  };
}

export default useInvoiceItemsViewItems;
